import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const RunCalcSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .refine((val) => val !== null, {
      message: "form.date_required",
    })
    .describe("date"),
});

export type RunCalcSchemaType = z.infer<typeof RunCalcSchema>;

type RunCalcPopoverProps = {
  client: string;
};

export function RunCalcPopover({ client }: RunCalcPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const currentPage = useCurrentPageFromUrl();

  const {
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { isValid },
    reset,
  } = useForm<RunCalcSchemaType>({
    mode: "onChange",
    defaultValues: {
      reportingDate: undefined,
    },
    resolver: zodResolver(RunCalcSchema) as Resolver<RunCalcSchemaType>,
  });

  const actionName = t("actions.categorize", {
    defaultValue: "Categorize",
  });

  const runCalcMutation = useMutation({
    mutationFn: async (data: RunCalcSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);
      if (!formattedDate) {
        throw new Error("reportingDate is required");
      }

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/categorize`,
        {
          reportingDate: formattedDate,
          prefix: client,
          client,
          action_button_name: actionName,
          action_execution_page: currentPage ?? "unknown",
        },
      );
    },
    onSuccess: () => {
      toast.success(t("calculations.started"));
      setIsOpen(false);
      reset();
    },
    onError: () => {
      toast.error(t("calculations.error"));
    },
  });

  const onSubmit = (data: RunCalcSchemaType) => {
    runCalcMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage:
              "linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {actionName}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1 w-full">
          <DateField
            name="reportingDate"
            label={t("templates.reporting_date", {
              defaultValue: "Date",
            })}
            watch={watch}
            setValue={setValue}
            register={register}
            required={true}
          />

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!isValid}
              isLoading={runCalcMutation.isPending}
            >
              {t("actions.confirm")}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export function C3RunCalcPopover() {
  return <RunCalcPopover client="c3" />;
}

export function S1RunCalcPopover() {
  return <RunCalcPopover client="s1" />;
}

export function I1RunCalcPopover() {
  return <RunCalcPopover client="i1" />;
}
